// src/components/fulfillment/OrgFormPdfPreviewModal.tsx

import { useEffect, useMemo, useState } from "react";
import { uploadRenderedFormPdf } from "@/components/opportunity/pdf/generateFormPdf";

export type OrgFormPdfPreviewModalProps = {
  open: boolean;
  blob: Blob | null;
  formInstanceId: string;
  formTitle: string;
  onSaved: (outputId: string) => void;
  onClose: () => void;
};

export function OrgFormPdfPreviewModal(props: OrgFormPdfPreviewModalProps) {
  const { open, blob, formInstanceId, formTitle, onSaved, onClose } = props;
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const blobUrl = useMemo(() => (blob ? URL.createObjectURL(blob) : null), [blob]);
  useEffect(() => {
    return () => {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [blobUrl]);

  if (!open) return null;

  async function save() {
    if (!blob) return;
    setSaving(true);
    setError(null);
    const r = await uploadRenderedFormPdf({ formInstanceId, blob });
    setSaving(false);
    if (!r.ok) {
      setError(r.error);
      return;
    }
    onSaved(r.outputId);
  }

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-black/60 px-4 py-6 sm:px-6">
      <div className="mx-auto flex h-full w-full max-w-6xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl dark:bg-zinc-900">
        <div className="flex items-start justify-between gap-3 border-b border-gray-200 px-5 py-3 dark:border-zinc-700">
          <div className="min-w-0">
            <h3 className="text-base font-bold text-gray-900 dark:text-zinc-100">Form preview</h3>
            <p className="mt-0.5 truncate text-xs text-gray-500 dark:text-zinc-400">{formTitle}</p>
            {error ? <p className="mt-1 text-xs text-red-700 dark:text-red-300">{error}</p> : null}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-zinc-700 dark:text-zinc-200 dark:hover:bg-zinc-800"
            >
              Close
            </button>
            <button
              type="button"
              onClick={() => void save()}
              disabled={saving || !blob}
              className="rounded-lg bg-indigo-600 px-3 py-1.5 text-sm text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save PDF"}
            </button>
          </div>
        </div>
        <div className="flex-1 bg-gray-100 dark:bg-zinc-950">
          {blobUrl ? (
            <iframe key={blobUrl} src={blobUrl} title="Form preview" className="h-full w-full border-0" />
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-gray-500 dark:text-zinc-400">
              No preview available.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
